import React, { useRef, useState } from 'react'
import { Play, Image as ImageIcon, Flame, ChevronLeft, ChevronRight } from 'lucide-react'

interface CapturedMediaProps {
  /** `adData` de la captura, ya con las URLs reescritas a MinIO si la media está archivada. */
  ad: any
  /** URLs originales de Meta, como respaldo si el objeto archivado no carga. */
  originalMedia?: { images?: string[]; videos?: string[] }
  /** Alto del visor. Se usa `object-contain`: el creativo nunca se recorta. */
  heightClass?: string
}

interface Slide {
  kind: 'video' | 'image'
  src: string
  /** Se intenta si `src` falla: la URL original de Meta. */
  fallback?: string
  poster?: string
}

/**
 * Extrae los creativos del anuncio, vídeos primero.
 *
 * Ojo con los vídeos archivados: `AdMediaProcessor` los reescribe a
 * `{video_hd_url, video_sd_url}` y PIERDE `video_preview_image_url`, así que muchos no
 * tienen póster. Por eso el <video> usa `preload="metadata"`, para que el navegador pinte
 * el primer fotograma en vez de dejar un hueco negro.
 */
function collectSlides(ad: any, originals?: { images?: string[]; videos?: string[] }): Slide[] {
  const media = ad?.apify_data || {}
  const slides: Slide[] = []

  media.videos?.forEach((v: any, i: number) => {
    const src = v?.video_hd_url || v?.video_sd_url || v?.watermarked_video_hd_url
    if (src) {
      slides.push({
        kind: 'video',
        src,
        fallback: originals?.videos?.[i],
        poster: v?.video_preview_image_url
      })
    }
  })

  media.images?.forEach((img: any, i: number) => {
    const src = typeof img === 'string' ? img : img?.url
    if (src) slides.push({ kind: 'image', src, fallback: originals?.images?.[i] })
  })

  if (slides.length === 0 && media.video_preview_image_url) {
    slides.push({ kind: 'image', src: media.video_preview_image_url })
  }

  return slides
}

export default function CapturedMedia({ ad, originalMedia, heightClass = 'h-64' }: CapturedMediaProps) {
  const [index, setIndex] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [failed, setFailed] = useState<Record<number, boolean>>({})
  /** Índices que ya cayeron al respaldo, para no entrar en bucle de errores. */
  const [usingFallback, setUsingFallback] = useState<Record<number, boolean>>({})
  const videoRef = useRef<HTMLVideoElement>(null)

  const slides = collectSlides(ad, originalMedia)
  const slide = slides[index]

  /** Primero el archivado; si falla y hay original de Meta, se prueba esa antes de rendirse. */
  const onMediaError = () => {
    if (slide?.fallback && !usingFallback[index]) {
      setUsingFallback(f => ({ ...f, [index]: true }))
      return
    }
    setFailed(f => ({ ...f, [index]: true }))
  }

  const currentSrc = slide && usingFallback[index] && slide.fallback ? slide.fallback : slide?.src

  // Sin creativo utilizable no se reserva el alto completo: una tarjeta con 224px de hueco
  // gris es peor que una tarjeta compacta. Basta una franja que explique qué pasa.
  if (!slide || failed[index]) {
    return (
      <div className="flex h-16 items-center gap-2 bg-stone-800/60 px-4 text-stone-500">
        {slides.length > 0 ? <ImageIcon className="h-4 w-4" /> : <Flame className="h-4 w-4" />}
        <span className="text-xs">
          {slides.length > 0 ? 'El creativo no carga' : 'Sin creativo archivado'}
        </span>
      </div>
    )
  }

  const go = (delta: number) => {
    setPlaying(false)
    setIndex(i => (i + delta + slides.length) % slides.length)
  }

  const toggle = () => {
    const el = videoRef.current
    if (!el) return
    if (el.paused) {
      void el.play().then(() => setPlaying(true)).catch(() => undefined)
    } else {
      el.pause()
      setPlaying(false)
    }
  }

  return (
    <div className={`group relative ${heightClass} bg-stone-950`}>
      {slide.kind === 'video' ? (
        <video
          ref={videoRef}
          key={currentSrc}
          src={currentSrc}
          poster={slide.poster}
          className="h-full w-full object-contain"
          muted
          loop
          playsInline
          preload="metadata"
          controls={playing}
          onClick={toggle}
          onError={onMediaError}
        />
      ) : (
        <img
          key={currentSrc}
          src={currentSrc}
          alt={ad?.ad_creative_link_titles?.[0] || 'Creativo del anuncio'}
          className="h-full w-full object-contain"
          loading="lazy"
          onError={onMediaError}
        />
      )}

      {slide.kind === 'video' && !playing && (
        <button
          onClick={toggle}
          className="absolute inset-0 flex items-center justify-center bg-stone-950/20 transition-colors hover:bg-stone-950/40"
          aria-label="Reproducir el anuncio"
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-stone-900/80 ring-1 ring-stone-600">
            <Play className="ml-0.5 h-5 w-5 text-stone-100" fill="currentColor" />
          </span>
        </button>
      )}

      {slides.length > 1 && (
        <>
          <button
            onClick={() => go(-1)}
            className="absolute left-1 top-1/2 -translate-y-1/2 rounded-full bg-stone-900/80 p-1.5 text-stone-200 opacity-0 ring-1 ring-stone-600 transition-opacity group-hover:opacity-100"
            aria-label="Creativo anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => go(1)}
            className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full bg-stone-900/80 p-1.5 text-stone-200 opacity-0 ring-1 ring-stone-600 transition-opacity group-hover:opacity-100"
            aria-label="Creativo siguiente"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <span className="absolute bottom-2 right-2 rounded-full bg-stone-900/85 px-2 py-0.5 text-xs text-stone-300 ring-1 ring-stone-700">
            {index + 1}/{slides.length}
          </span>
        </>
      )}
    </div>
  )
}
